'use client';

import { useMemo } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  EntityIcon,
  MetricItem,
  Table,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  GRAPH_NODE_ICON,
  isRelatedRouteEntityKind,
  type RelatedRouteAsset,
} from '@terra/ui-graphs/external-network';
import { getExternalNetworkEntityKindLabel } from './surfaceAsset.constants';
import { AssetRowIcon } from './AssetRowIcon';

const RELATED_ASSETS_ROW_HEIGHT = 56;

export function AssetRelatedAssetsTable({
  assets,
  onOpenAsset,
}: {
  assets: RelatedRouteAsset[];
  onOpenAsset: (asset: RelatedRouteAsset) => void;
}) {
  const tAssets = useTranslations('aiApplications.assets');
  const tDetections = useTranslations('aiApplications.componentDrawer.tabs');
  const tEntityKinds = useTranslations('externalNetwork.entityKinds');
  const tCards = useTranslations('designSystem.applicationAssets.cards');
  const tLayout = useTranslations('layout');
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  );
  const columnDefs = useMemo<ColDef<RelatedRouteAsset>[]>(
    () => [
      {
        colId: 'name',
        headerName: tAssets('assetName'),
        field: 'label',
        flex: 2,
        cellRenderer: ({ data }: ICellRendererParams<RelatedRouteAsset>) =>
          data ? <RelatedAssetNameCell asset={data} /> : null,
      },
      {
        colId: 'type',
        headerName: tAssets('type'),
        flex: 1,
        valueGetter: ({ data }) => {
          if (!data) {
            return '';
          }

          return isRelatedRouteEntityKind(data.kind)
            ? getExternalNetworkEntityKindLabel({
                kind: data.kind,
                t: tEntityKinds,
              })
            : tLayout('webApplications');
        },
      },
      {
        colId: 'detections',
        headerName: tDetections('detections'),
        flex: 1,
        cellRenderer: ({ data }: ICellRendererParams<RelatedRouteAsset>) =>
          data ? (
            <RelatedAssetDetectionsCell
              signalsCount={data.signalsCount}
              signalsLabel={tCards('signals', { count: data.signalsCount })}
            />
          ) : null,
      },
    ],
    [tAssets, tCards, tDetections, tEntityKinds, tLayout],
  );

  if (assets.length === 0) {
    return <BodyDefault color="fg.muted">{tAssets('empty')}</BodyDefault>;
  }

  return (
    <Box w="full">
      <Table<RelatedRouteAsset>
        rowData={assets}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        hideColumnControls
        suppressCellFocus
        rowHeight={RELATED_ASSETS_ROW_HEIGHT}
        onRowClicked={(event) => {
          if (event.data) {
            onOpenAsset(event.data);
          }
        }}
      />
    </Box>
  );
}

function RelatedAssetNameCell({ asset }: { asset: RelatedRouteAsset }) {
  return (
    <HStack h="full" gap={2} minW={0} alignItems="center">
      <AssetRowIcon icon={GRAPH_NODE_ICON[asset.kind]} />
      <BodyDefault color="fg.default" truncate>
        {asset.label}
      </BodyDefault>
    </HStack>
  );
}

function RelatedAssetDetectionsCell({
  signalsCount,
  signalsLabel,
}: {
  signalsCount: number;
  signalsLabel: string;
}) {
  return (
    <HStack h="full" gap={5} alignItems="center">
      <MetricItem
        icon={
          <EntityIcon type="signal" appearance="default" density="compact" />
        }
        label={signalsLabel}
        metrics={signalsCount}
      />
    </HStack>
  );
}
